/**
 * Bench Management API
 */
import apiClient from './apiClient';

export interface BenchEmployee {
  employee_id: number;
  employee_code: string;
  employee_name: string;
  email: string;
  category: string | null;
  department_id: number | null;
  department_name: string | null;
  manager_id: number | null;
  manager_name: string | null;
  practice: string | null;
  designation: string | null;
  expected_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  total_filled_hours: number;
  bench_hours: number;
  utilization: number;
  leave_hours: number;
  working_days: number;
}

export interface BenchEmployeesResponse {
  employees: BenchEmployee[];
  count: number;
  year: number;
  month: number;
  date: string | null;
}

export interface BenchSummary {
  bench_employee_count: number;
  total_bench_hours: number;
  average_utilization: number;
  month: number;
  year: number;
}

export interface EmployeeBenchData {
  employee_id: number;
  year: number;
  month: number;
  expected_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  total_filled_hours: number;
  bench_hours: number;
  utilization: number;
  leave_hours: number;
  working_days: number;
}

const benchApi = {
  /**
   * Get list of employees on bench
   */
  getBenchEmployees: async (params?: {
    year?: number;
    month?: number;
    date?: string;
    department_id?: number;
    category?: string;
    min_bench_hours?: number;
  }): Promise<BenchEmployeesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());
    if (params?.date) queryParams.append('date', params.date);
    if (params?.department_id) queryParams.append('department_id', params.department_id.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.min_bench_hours !== undefined) queryParams.append('min_bench_hours', params.min_bench_hours.toString());
    
    const query = queryParams.toString();
    return apiClient.get<BenchEmployeesResponse>(`/bench/employees${query ? `?${query}` : ''}`);
  },

  /**
   * Get bench summary statistics
   */
  getBenchSummary: async (params?: {
    year?: number;
    month?: number;
  }): Promise<BenchSummary> => {
    const queryParams = new URLSearchParams();
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());
    
    const query = queryParams.toString();
    return apiClient.get<BenchSummary>(`/bench/summary${query ? `?${query}` : ''}`);
  },

  /**
   * Get bench data for a specific employee
   */
  getEmployeeBenchData: async (
    employeeId: number,
    params?: { year?: number; month?: number }
  ): Promise<EmployeeBenchData> => {
    const queryParams = new URLSearchParams();
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());
    
    const query = queryParams.toString();
    return apiClient.get<EmployeeBenchData>(`/bench/employee/${employeeId}${query ? `?${query}` : ''}`);
  },
};

export default benchApi;
